import * as actionTypes from './actionTypes';
import { DocumentActionTypes } from './actionTypes';
import { HttpError } from '../../config/Axios/axios-instance';
import { Document, DocumentTaskStatus } from '../../domain/Document';

export type DocumentStateType = {
  documentsListUpdateNeeded: boolean;
  documentCreateLoading: boolean;
  documentCreateError: HttpError;
  documentCreateSuccess: boolean;
  createdDocument: Document | null;
  document: Document | null;
  documentLoading: boolean;
  documentError: HttpError;
  documentUpdateLoading: boolean;
  documentUpdateError: HttpError;
  documentUpdateSuccess: boolean;
  archiveDocumentLoading: boolean;
  toggleArchiveDocumentSuccess: boolean;
  toggleArchiveDocumentError: HttpError;
  documentTaskStatus: DocumentTaskStatus | null;
  documentTaskStatusLoading: boolean;
  documentTaskStatusError: HttpError;
};

export type DocumentActionType = DocumentStateType & {
  type: DocumentActionTypes;
};

export const initialState: DocumentStateType = {
  documentsListUpdateNeeded: false,
  documentCreateLoading: false,
  documentCreateError: null,
  documentCreateSuccess: false,
  createdDocument: null,
  document: null,
  documentLoading: false,
  documentError: null,
  documentUpdateLoading: false,
  documentUpdateError: null,
  documentUpdateSuccess: false,
  archiveDocumentLoading: false,
  toggleArchiveDocumentSuccess: false,
  toggleArchiveDocumentError: null,
  documentTaskStatus: null,
  documentTaskStatusLoading: false,
  documentTaskStatusError: null,
};

const fetchDocumentStart = (state: DocumentStateType): DocumentStateType => ({
  ...state,
  documentLoading: true,
  documentCreateSuccess: false,
  documentCreateError: null,
});

const fetchDocumentSuccess = (
  state: DocumentStateType,
  action: DocumentActionType,
): DocumentStateType => ({
  ...state,
  document: action.document,
  documentLoading: false,
  documentError: null,
});

const fetchDocumentFail = (
  state: DocumentStateType,
  action: DocumentActionType,
): DocumentStateType => ({
  ...state,
  documentError: action.documentError,
  documentLoading: false,
});

const createDocumentStart = (state: DocumentStateType): DocumentStateType => ({
  ...state,
  documentCreateLoading: true,
});

const createDocumentSuccess = (
  state: DocumentStateType,
  action: DocumentActionType,
): DocumentStateType => ({
  ...state,
  documentCreateLoading: false,
  documentCreateError: null,
  documentCreateSuccess: true,
  createdDocument: action.createdDocument,
});

const createDocumentFail = (
  state: DocumentStateType,
  action: DocumentActionType,
): DocumentStateType => ({
  ...state,
  documentCreateLoading: false,
  documentCreateError: action.documentCreateError,
});

const updateDocumentStart = (state: DocumentStateType): DocumentStateType => ({
  ...state,
  documentUpdateLoading: true,
});

const updateDocumentSuccess = (
  state: DocumentStateType,
): DocumentStateType => ({
  ...state,
  documentUpdateLoading: false,
  documentUpdateError: null,
  documentUpdateSuccess: true,
});

const updateDocumentFail = (
  state: DocumentStateType,
  action: DocumentActionType,
): DocumentStateType => ({
  ...state,
  documentUpdateLoading: false,
  documentUpdateError: action.documentUpdateError,
});

const toggleArchiveDocumentStart = (
  state: DocumentStateType,
): DocumentStateType => ({
  ...state,
  archiveDocumentLoading: true,
  toggleArchiveDocumentSuccess: false,
});

const toggleArchiveDocumentSuccess = (
  state: DocumentStateType,
): DocumentStateType => ({
  ...state,
  archiveDocumentLoading: false,
  toggleArchiveDocumentError: null,
  toggleArchiveDocumentSuccess: true,
});

const toggleArchiveDocumentFail = (
  state: DocumentStateType,
  action: DocumentActionType,
): DocumentStateType => ({
  ...state,
  archiveDocumentLoading: false,
  toggleArchiveDocumentError: action.toggleArchiveDocumentError,
});

const fetchDocumentTaskStatusStart = (
  state: DocumentStateType,
): DocumentStateType => ({
  ...state,
  documentTaskStatusLoading: true,
});

const fetchDocumentTaskStatusSuccess = (
  state: DocumentStateType,
  action: DocumentActionType,
): DocumentStateType => ({
  ...state,
  documentTaskStatus: action.documentTaskStatus,
  documentTaskStatusLoading: false,
  documentTaskStatusError: null,
});

const fetchDocumentTaskStatusFail = (
  state: DocumentStateType,
  action: DocumentActionType,
): DocumentStateType => ({
  ...state,
  documentTaskStatusError: action.documentError,
  documentTaskStatusLoading: false,
});

const resetDocumentCreateUpdateState = (
  state: DocumentStateType,
): DocumentStateType => ({
  ...state,
  documentCreateSuccess: false,
  toggleArchiveDocumentSuccess: false,
});

const resetDocumentStore = (): DocumentStateType => ({
  documentsListUpdateNeeded: false,
  documentCreateLoading: false,
  documentCreateError: null,
  documentCreateSuccess: false,
  createdDocument: null,
  document: null,
  documentLoading: false,
  documentError: null,
  documentUpdateLoading: false,
  documentUpdateError: null,
  documentUpdateSuccess: false,
  archiveDocumentLoading: false,
  toggleArchiveDocumentSuccess: false,
  toggleArchiveDocumentError: null,
  documentTaskStatus: null,
  documentTaskStatusLoading: false,
  documentTaskStatusError: null,
});

const reducer = (state = initialState, action: DocumentActionType) => {
  switch (action.type) {
    case actionTypes.FETCH_DOCUMENT_START:
      return fetchDocumentStart(state);
    case actionTypes.FETCH_DOCUMENT_SUCCESS:
      return fetchDocumentSuccess(state, action);
    case actionTypes.FETCH_DOCUMENT_FAIL:
      return fetchDocumentFail(state, action);
    case actionTypes.CREATE_DOCUMENT_START:
      return createDocumentStart(state);
    case actionTypes.CREATE_DOCUMENT_SUCCESS:
      return createDocumentSuccess(state, action);
    case actionTypes.CREATE_DOCUMENT_FAIL:
      return createDocumentFail(state, action);
    case actionTypes.RESET_DOCUMENT_CREATE_STATE:
      return resetDocumentCreateUpdateState(state);
    case actionTypes.UPDATE_DOCUMENT_START:
      return updateDocumentStart(state);
    case actionTypes.UPDATE_DOCUMENT_SUCCESS:
      return updateDocumentSuccess(state);
    case actionTypes.UPDATE_DOCUMENT_FAIL:
      return updateDocumentFail(state, action);
    case actionTypes.TOGGLE_ARCHIVE_DOCUMENT_START:
      return toggleArchiveDocumentStart(state);
    case actionTypes.TOGGLE_ARCHIVE_DOCUMENT_SUCCESS:
      return toggleArchiveDocumentSuccess(state);
    case actionTypes.TOGGLE_ARCHIVE_DOCUMENT_FAIL:
      return toggleArchiveDocumentFail(state, action);
    case actionTypes.FETCH_DOCUMENT_TASK_STATUS_START:
      return fetchDocumentTaskStatusStart(state);
    case actionTypes.FETCH_DOCUMENT_TASK_STATUS_SUCCESS:
      return fetchDocumentTaskStatusSuccess(state, action);
    case actionTypes.FETCH_DOCUMENT_TASK_STATUS_FAIL:
      return fetchDocumentTaskStatusFail(state, action);
    case actionTypes.RESET_DOCUMENT_STORE:
      return resetDocumentStore();
    default:
      return state;
  }
};

export default reducer;
